import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";

// PR profile update hook — mirrors use_company_update_api: instantiated by an edit-modal body
// from the provider context's setters, exposes updatePrProfile(data). On success it preserves the
// two side-effects the old usePrProfileState carried: soft-update the URL when the slug changes
// (no reload) and refresh the document title; then silently reloads the page data.
//
// updateById now throws (throwOnApiError) on any sub-request failure, so reaching the code after it =
// success. A failure is caught here and surfaced via generalError — the modal-local alert that keeps
// the modal open. We deliberately do NOT use asyncError for a save failure: asyncError is re-thrown to
// the page error boundary (PrProfile.jsx), which would tear down the whole profile page for what is a
// recoverable, retryable save error.
//
// updateById issues two sequential PATCHes (name via change_name_and_slug, then the generic update
// endpoint), so a failure can be a partial save: the name may already be persisted server-side when the
// second PATCH throws. The failure path therefore re-syncs page state from the server via silentReload
// rather than assuming nothing changed (a failed first PATCH makes the reload a harmless refetch). That
// reconcile reload is marked nonfatal (and its own await is guarded): if the save failed because the
// backend/network is down the reconcile read will fail too, and it must not escalate the recoverable
// generalError into the page error boundary — nor let a rejecting reload leak an unhandled rejection out.
const usePrProfileUpdateApi = ({ prProfileId, setIsUpdating, setGeneralError, setOpenEditModal, silentReload }) => {
  const updatePrProfile = async (data, options = {}) => {
    const { closeModal = true } = options;

    try {
      setIsUpdating(true);
      setGeneralError(null);

      qahoy.track("PrProfiles#updatePrProfile");

      const response = await qapi.prProfiles.updateById(prProfileId, data);

      const slug = response.data?.data?.attributes?.slug;
      // V1-COMPAT: skip on V1 — its equivalent page is the id-based /pr_users/:id (see
      // prProfileUrl in views_shared/utils/urls), which a slug change never affects, so there is no
      // address-bar path to canonicalize into.
      if (slug && !globalThis.Qwoted?.V1_HOSTED) {
        window.history.replaceState(null, "", `/pr_profiles/${slug}`);
      }
      const name = response.data?.data?.attributes?.name;
      if (name) {
        document.title = `${name} - Qwoted`;
      }

      if (closeModal) setOpenEditModal(null);
      await silentReload();
    } catch (error) {
      setGeneralError(error);
      try {
        await silentReload({ nonfatal: true });
      } catch {
        // The generalError already tells the user the save failed; a rejecting reconcile reload must
        // never propagate an unhandled rejection out of the submit handler.
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return { updatePrProfile };
};

export default usePrProfileUpdateApi;
